import { createUseStyles } from 'react-jss';
import { ChipSkins } from '../types/index.js';
import { Timings } from '../../../../foundation/timings/timings/index.js';
import { Easing } from '../../../../foundation/easing/easing/index.js';
import { FontWeights } from '../../../../foundation/fonts/fonts/index.js';
import { Spacings } from '../../../../foundation/spacings/spacings/index.js';
import { BreakPointsMedia } from '../../../../foundation/responsive/responsive/index.js';
import { ControlBackgroundColors } from '../../../../foundation/colors/controlBackgroundColors/controlBackgroundColors/index.js';
import { ControlEndIconColors } from '../../../../foundation/colors/controlEndIconColors/controlEndIconColors/index.js';
import { ControlTextColors } from '../../../../foundation/colors/controlTextColors/controlTextColors/index.js';
import { ControlStartIconColors } from '../../../../foundation/colors/controlStartIconColors/controlStartIconColors/index.js';
import { ControlBorderColors } from '../../../../foundation/colors/controlBorderColors/controlBorderColors/index.js';
import { canTouch, canHover } from '../../../../foundation/styleHelpers/hoverTouch/hoverTouch/index.js';
import { ErrorColors } from '../../../../foundation/colors/errorColors/errorColors/index.js';
import { WarningColors } from '../../../../foundation/colors/warningColors/warningColors/index.js';
import { SuccessColors } from '../../../../foundation/colors/successColors/successColors/index.js';
import { NegativeColors } from '../../../../foundation/colors/negativeColors/negativeColors/index.js';
import { SecondaryColors } from '../../../../foundation/colors/secondaryColors/secondaryColors/index.js';
import { PrimaryColors } from '../../../../foundation/colors/primaryColors/primaryColors/index.js';

const BORDER_WIDTH_KEY = '--border-width';
const BORDER_COLOR_KEY = '--border-color';
const START_ICON_COLOR_KEY = '--start-icon-color';
const TEXT_COLOR_KEY = '--text-color';
const END_ICON_COLOR_KEY = '--end-icon-color';
const BG_COLOR_KEY = '--bg-color';
const generateModel = (borderColor, startIconColor, textColor, endIconColor, backgroundColor) => ({
    [BORDER_COLOR_KEY]: `${borderColor}`,
    [START_ICON_COLOR_KEY]: `${startIconColor}`,
    [TEXT_COLOR_KEY]: `${textColor}`,
    [END_ICON_COLOR_KEY]: `${endIconColor}`,
    [BG_COLOR_KEY]: `${backgroundColor}`,
});
const createDefaultStyles = (type) => generateModel(type === 'active' || type === 'activeAction' ? ControlBackgroundColors[type] : ControlBorderColors[type], ControlStartIconColors[type], ControlTextColors[type], ControlEndIconColors[type], ControlBackgroundColors[type]);
const createInteractiveStyles = (type, interactivePackage) => generateModel(interactivePackage.base[type], interactivePackage.text[type], interactivePackage.text[type], interactivePackage.text[type], interactivePackage.base[type]);
const defaultStyles = createDefaultStyles('default');
const defaultActionStyles = createDefaultStyles('action');
const defaultActiveStyles = createDefaultStyles('active');
const defaultActiveActionStyles = createDefaultStyles('activeAction');
const interactiveActive = (interactivePackage) => createInteractiveStyles('active', interactivePackage);
const interactiveActiveAction = (interactivePackage) => createInteractiveStyles('activeAction', interactivePackage);
const getStyle = (defaultStyle, actionStyle) => ({
    ...defaultStyle,
    [canHover]: {
        '&:hover': {
            ...actionStyle,
        },
    },
    [canTouch]: {
        '&:active': {
            ...actionStyle,
        },
    },
});
const getDefaultStyles = getStyle(defaultStyles, defaultActionStyles);
const getDefaultActiveStyles = getStyle(defaultActiveStyles, defaultActiveActionStyles);
const getActiveStyle = (interactivePackage) => getStyle(interactiveActive(interactivePackage), interactiveActiveAction(interactivePackage));
const getStyleForSkin = (activeStyle) => ({
    ...getDefaultStyles,
    '&[data-is-active="true"]': activeStyle,
});
const getStyleForInteractiveSkin = (interactivePackage) => getStyleForSkin(getActiveStyle(interactivePackage));
const useSkinStyles = createUseStyles({
    '@keyframes rotate': {
        to: {
            transform: 'rotate(360deg)',
        },
    },
    loadingIcon: {
        animation: `$rotate ${Timings.long} ${Easing.linear} infinite`,
    },
    [ChipSkins.Default]: getStyleForSkin(getDefaultActiveStyles),
    [ChipSkins.Primary]: getStyleForInteractiveSkin(PrimaryColors),
    [ChipSkins.Secondary]: getStyleForInteractiveSkin(SecondaryColors),
    [ChipSkins.Tertiary]: getStyleForInteractiveSkin(NegativeColors),
    [ChipSkins.Positive]: getStyleForInteractiveSkin(NegativeColors),
    [ChipSkins.Negative]: getStyleForInteractiveSkin(NegativeColors),
    [ChipSkins.Success]: getStyleForInteractiveSkin(SuccessColors),
    [ChipSkins.Warning]: getStyleForInteractiveSkin(WarningColors),
    [ChipSkins.Error]: getStyleForInteractiveSkin(ErrorColors),
}, {
    name: 'chip-skin',
});
const useLayoutStyles = createUseStyles({
    root: {
        [BORDER_WIDTH_KEY]: '1px',
        display: 'inline-grid',
        gridTemplateColumns: ({ hasClose }) => (hasClose ? '1fr auto' : 'auto'),
        alignItems: 'center',
        justifyContent: 'center',
        gap: Spacings.s4,
        verticalAlign: 'top',
        width: ({ isFullWidth }) => (isFullWidth && '100%'),
        maxWidth: '100%',
        border: `var(${BORDER_WIDTH_KEY}) solid var(${BORDER_COLOR_KEY})`,
        backgroundColor: `var(${BG_COLOR_KEY})`,
        borderRadius: ({ sizes }) => (sizes.Default.value?.borderRadius),
        height: ({ sizes }) => (sizes.Default.value?.height),
        transitionProperty: 'background-color, border',
        transitionTimingFunction: Easing.ease,
        transitionDuration: Timings.default,
        cursor: 'pointer',
        overflow: 'hidden',
        position: 'relative',
        pointerEvents: ({ isLoading }) => (isLoading) && 'none',
        [BreakPointsMedia.XXL]: {
            borderRadius: ({ sizes }) => (sizes.XXL.value?.borderRadius),
            height: ({ sizes }) => (sizes.XXL.value?.height),
        },
        [BreakPointsMedia.XL]: {
            borderRadius: ({ sizes }) => (sizes.XL.value?.borderRadius),
            height: ({ sizes }) => (sizes.XL.value?.height),
        },
        [BreakPointsMedia.LG]: {
            borderRadius: ({ sizes }) => (sizes.LG.value?.borderRadius),
            height: ({ sizes }) => (sizes.LG.value?.height),
        },
        [BreakPointsMedia.MD]: {
            borderRadius: ({ sizes }) => (sizes.MD.value?.borderRadius),
            height: ({ sizes }) => (sizes.MD.value?.height),
        },
        [BreakPointsMedia.SM]: {
            borderRadius: ({ sizes }) => (sizes.SM.value?.borderRadius),
            height: ({ sizes }) => (sizes.SM.value?.height),
        },
        [BreakPointsMedia.XS]: {
            borderRadius: ({ sizes }) => (sizes.XS.value?.borderRadius),
            height: ({ sizes }) => (sizes.XS.value?.height),
        },
        [BreakPointsMedia.XXS]: {
            borderRadius: ({ sizes }) => (sizes.XXS.value?.borderRadius),
            height: ({ sizes }) => (sizes.XXS.value?.height),
        },
    },
    interactiveArea: {
        minWidth: 0,
        display: 'flex',
        gap: ({ sizes }) => sizes.Default.value?.gap,
        alignItems: 'center',
        padding: ({ sizes }) => `${sizes.Default.value?.paddingVertical} ${sizes.Default.value?.paddingHorizontal}`,
        paddingInlineEnd: ({ hasClose }) => hasClose && Spacings.s0,
        visibility: ({ isLoading }) => isLoading && 'hidden',
        [BreakPointsMedia.XXL]: {
            padding: ({ sizes }) => `${sizes.XXL.value?.paddingVertical} ${sizes.XXL.value?.paddingHorizontal}`,
            paddingInlineEnd: ({ hasClose }) => hasClose && Spacings.s0,
        },
        [BreakPointsMedia.XL]: {
            padding: ({ sizes }) => `${sizes.XL.value?.paddingVertical} ${sizes.XL.value?.paddingHorizontal}`,
            paddingInlineEnd: ({ hasClose }) => hasClose && Spacings.s0,
        },
        [BreakPointsMedia.LG]: {
            padding: ({ sizes }) => `${sizes.LG.value?.paddingVertical} ${sizes.LG.value?.paddingHorizontal}`,
            paddingInlineEnd: ({ hasClose }) => hasClose && Spacings.s0,
        },
        [BreakPointsMedia.MD]: {
            padding: ({ sizes }) => `${sizes.MD.value?.paddingVertical} ${sizes.MD.value?.paddingHorizontal}`,
            paddingInlineEnd: ({ hasClose }) => hasClose && Spacings.s0,
        },
        [BreakPointsMedia.SM]: {
            padding: ({ sizes }) => `${sizes.SM.value?.paddingVertical} ${sizes.SM.value?.paddingHorizontal}`,
            paddingInlineEnd: ({ hasClose }) => hasClose && Spacings.s0,
        },
        [BreakPointsMedia.XS]: {
            padding: ({ sizes }) => `${sizes.XS.value?.paddingVertical} ${sizes.XS.value?.paddingHorizontal}`,
            paddingInlineEnd: ({ hasClose }) => hasClose && Spacings.s0,
        },
        [BreakPointsMedia.XXS]: {
            padding: ({ sizes }) => `${sizes.XXS.value?.paddingVertical} ${sizes.XXS.value?.paddingHorizontal}`,
            paddingInlineEnd: ({ hasClose }) => hasClose && Spacings.s0,
        },
    },
    icon: {
        minWidth: 0,
        flex: ({ sizes }) => `0 0 ${sizes.Default.value?.iconSize}px`,
        width: ({ sizes }) => sizes.Default.value?.iconSize,
        height: ({ sizes }) => sizes.Default.value?.iconSize,
        [BreakPointsMedia.XXL]: {
            flex: ({ sizes }) => `0 0 ${sizes.XXL.value?.iconSize}px`,
            width: ({ sizes }) => sizes.XXL.value?.iconSize,
            height: ({ sizes }) => sizes.XXL.value?.iconSize,
        },
        [BreakPointsMedia.XL]: {
            flex: ({ sizes }) => `0 0 ${sizes.XL.value?.iconSize}px`,
            width: ({ sizes }) => sizes.XL.value?.iconSize,
            height: ({ sizes }) => sizes.XL.value?.iconSize,
        },
        [BreakPointsMedia.LG]: {
            flex: ({ sizes }) => `0 0 ${sizes.LG.value?.iconSize}px`,
            width: ({ sizes }) => sizes.LG.value?.iconSize,
            height: ({ sizes }) => sizes.LG.value?.iconSize,
        },
        [BreakPointsMedia.MD]: {
            flex: ({ sizes }) => `0 0 ${sizes.MD.value?.iconSize}px`,
            width: ({ sizes }) => sizes.MD.value?.iconSize,
            height: ({ sizes }) => sizes.MD.value?.iconSize,
        },
        [BreakPointsMedia.SM]: {
            flex: ({ sizes }) => `0 0 ${sizes.SM.value?.iconSize}px`,
            width: ({ sizes }) => sizes.SM.value?.iconSize,
            height: ({ sizes }) => sizes.SM.value?.iconSize,
        },
        [BreakPointsMedia.XS]: {
            flex: ({ sizes }) => `0 0 ${sizes.XS.value?.iconSize}px`,
            width: ({ sizes }) => sizes.XS.value?.iconSize,
            height: ({ sizes }) => sizes.XS.value?.iconSize,
        },
        [BreakPointsMedia.XXS]: {
            flex: ({ sizes }) => `0 0 ${sizes.XXS.value?.iconSize}px`,
            width: ({ sizes }) => sizes.XXS.value?.iconSize,
            height: ({ sizes }) => sizes.XXS.value?.iconSize,
        },
        '& path': {
            fill: `var(${START_ICON_COLOR_KEY})`,
            transition: `fill ${Timings.default} ${Easing.ease}`,
        },
    },
    loadingIcon: {
        position: 'absolute',
        inset: 0,
        margin: 'auto',
    },
    textWrap: {
        minWidth: 0,
        flexGrow: 1,
        color: `var(${TEXT_COLOR_KEY})`,
        textAlign: 'center',
        wordBreak: 'break-all',
    },
    text: {
        width: '100%',
        color: `var(${TEXT_COLOR_KEY})`,
        textAlign: 'center',
        wordBreak: 'break-all',
        maxWidth: '100%',
        display: 'block',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        transition: `color ${Timings.default} ${Easing.ease}`,
        fontWeight: FontWeights.semibold,
        fontSize: ({ sizes }) => sizes.Default.value?.fontSize,
        lineHeight: ({ sizes }) => sizes.Default.value?.lineHeight,
        [BreakPointsMedia.XXL]: {
            fontSize: ({ sizes }) => sizes.XXL.value?.fontSize,
            lineHeight: ({ sizes }) => sizes.XXL.value?.lineHeight,
        },
        [BreakPointsMedia.XL]: {
            fontSize: ({ sizes }) => sizes.XL.value?.fontSize,
            lineHeight: ({ sizes }) => sizes.XL.value?.lineHeight,
        },
        [BreakPointsMedia.LG]: {
            fontSize: ({ sizes }) => sizes.LG.value?.fontSize,
            lineHeight: ({ sizes }) => sizes.LG.value?.lineHeight,
        },
        [BreakPointsMedia.MD]: {
            fontSize: ({ sizes }) => sizes.MD.value?.fontSize,
            lineHeight: ({ sizes }) => sizes.MD.value?.lineHeight,
        },
        [BreakPointsMedia.SM]: {
            fontSize: ({ sizes }) => sizes.SM.value?.fontSize,
            lineHeight: ({ sizes }) => sizes.SM.value?.lineHeight,
        },
        [BreakPointsMedia.XS]: {
            fontSize: ({ sizes }) => sizes.XS.value?.fontSize,
            lineHeight: ({ sizes }) => sizes.XS.value?.lineHeight,
        },
        [BreakPointsMedia.XXS]: {
            fontSize: ({ sizes }) => sizes.XXS.value?.fontSize,
            lineHeight: ({ sizes }) => sizes.XXS.value?.lineHeight,
        },
    },
    badge: {
        minWidth: 0,
    },
    dismissButton: {
        display: 'grid',
        placeItems: 'center',
        minWidth: 0,
        cursor: 'pointer',
        width: ({ sizes }) => sizes.Default.value?.endButtonSize,
        height: ({ sizes }) => sizes.Default.value?.endButtonSize,
        [BreakPointsMedia.XXL]: {
            width: ({ sizes }) => sizes.XXL.value?.endButtonSize,
            height: ({ sizes }) => sizes.XXL.value?.endButtonSize,
        },
        [BreakPointsMedia.XL]: {
            width: ({ sizes }) => sizes.XL.value?.endButtonSize,
            height: ({ sizes }) => sizes.XL.value?.endButtonSize,
        },
        [BreakPointsMedia.LG]: {
            width: ({ sizes }) => sizes.LG.value?.endButtonSize,
            height: ({ sizes }) => sizes.LG.value?.endButtonSize,
        },
        [BreakPointsMedia.MD]: {
            width: ({ sizes }) => sizes.MD.value?.endButtonSize,
            height: ({ sizes }) => sizes.MD.value?.endButtonSize,
        },
        [BreakPointsMedia.SM]: {
            width: ({ sizes }) => sizes.SM.value?.endButtonSize,
            height: ({ sizes }) => sizes.SM.value?.endButtonSize,
        },
        [BreakPointsMedia.XS]: {
            width: ({ sizes }) => sizes.XS.value?.endButtonSize,
            height: ({ sizes }) => sizes.XS.value?.endButtonSize,
        },
        [BreakPointsMedia.XXS]: {
            width: ({ sizes }) => sizes.XXS.value?.endButtonSize,
            height: ({ sizes }) => sizes.XXS.value?.endButtonSize,
        },
    },
    dismissIcon: {
        width: ({ sizes }) => sizes.Default.value?.endIconSize,
        height: ({ sizes }) => sizes.Default.value?.endIconSize,
        [BreakPointsMedia.XXL]: {
            width: ({ sizes }) => sizes.XXL.value?.endIconSize,
            height: ({ sizes }) => sizes.XXL.value?.endIconSize,
        },
        [BreakPointsMedia.XL]: {
            width: ({ sizes }) => sizes.XL.value?.endIconSize,
            height: ({ sizes }) => sizes.XL.value?.endIconSize,
        },
        [BreakPointsMedia.LG]: {
            width: ({ sizes }) => sizes.LG.value?.endIconSize,
            height: ({ sizes }) => sizes.LG.value?.endIconSize,
        },
        [BreakPointsMedia.MD]: {
            width: ({ sizes }) => sizes.MD.value?.endIconSize,
            height: ({ sizes }) => sizes.MD.value?.endIconSize,
        },
        [BreakPointsMedia.SM]: {
            width: ({ sizes }) => sizes.SM.value?.endIconSize,
            height: ({ sizes }) => sizes.SM.value?.endIconSize,
        },
        [BreakPointsMedia.XS]: {
            width: ({ sizes }) => sizes.XS.value?.endIconSize,
            height: ({ sizes }) => sizes.XS.value?.endIconSize,
        },
        [BreakPointsMedia.XXS]: {
            width: ({ sizes }) => sizes.XXS.value?.endIconSize,
            height: ({ sizes }) => sizes.XXS.value?.endIconSize,
        },
        '& path': {
            fill: `var(${END_ICON_COLOR_KEY})`,
            transition: `fill ${Timings.default} ${Easing.ease}`,
        },
    },
}, {
    name: 'chip',
});

export { useLayoutStyles, useSkinStyles };
